import { useMemo, useEffect, useState, useRef, useCallback } from "react";
import { ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";

const HERO_PRIORITY_SOURCES = ["New York Times", "Washington Post", "Washington Post Politics"];

const REGION_MAP = {
  americas: ["us", "ca"],
  europe: ["gb", "za"],
  asia: ["jp", "hk", "qa", "in", "sg"],
  africa: ["ng"],
  oceania: ["au"],
};

const getRegion = (country) => {
  for (const [region, codes] of Object.entries(REGION_MAP)) {
    if (codes.includes(country)) return region;
  }
  return "other";
};

const HeroCarousel = ({ articles, formatDate, onArticleClick, getFallbackImage }) => {
  const [heroSlide, setHeroSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const autoResumeTimer = useRef(null);

  const heroSlides = useMemo(() => {
    if (articles.length < 4) return [articles.slice(0, 4)];

    const used = new Set();
    const slides = [];

    const priorityMain = articles.find(
      a => HERO_PRIORITY_SOURCES.includes(a.source) && a.image_url && !used.has(a.url)
    );
    if (priorityMain) {
      used.add(priorityMain.url);
      const side = [];
      const sideRegions = new Set();
      sideRegions.add(getRegion(priorityMain.country));

      const japanArticle = articles.find(a => a.country === "jp" && !used.has(a.url));
      if (japanArticle) {
        side.push(japanArticle);
        used.add(japanArticle.url);
        sideRegions.add("asia");
      }
      for (const a of articles) {
        if (used.has(a.url)) continue;
        const r = getRegion(a.country);
        if (!sideRegions.has(r)) {
          side.push(a);
          used.add(a.url);
          sideRegions.add(r);
          if (side.length >= 3) break;
        }
      }
      for (const a of articles) {
        if (side.length >= 3) break;
        if (!used.has(a.url)) { side.push(a); used.add(a.url); }
      }
      slides.push([priorityMain, ...side.slice(0, 3)]);
    }

    const remaining = articles.filter(a => !used.has(a.url));
    for (let i = 0; i < remaining.length; i += 4) {
      const group = remaining.slice(i, i + 4);
      if (group.length < 2) break;
      const mainIdx = group.findIndex(a => a.image_url);
      if (mainIdx > 0) {
        [group[0], group[mainIdx]] = [group[mainIdx], group[0]];
      }
      while (group.length < 4 && remaining[i + group.length]) {
        group.push(remaining[i + group.length]);
      }
      slides.push(group);
      if (slides.length >= 8) break;
    }

    return slides.length > 0 ? slides : [articles.slice(0, 4)];
  }, [articles]);

  // Pause auto-scroll temporarily after manual interaction, resume after 15s
  const pauseAndResume = useCallback(() => {
    setPaused(true);
    if (autoResumeTimer.current) clearTimeout(autoResumeTimer.current);
    autoResumeTimer.current = setTimeout(() => setPaused(false), 15000);
  }, []);

  const goNext = useCallback(() => {
    setHeroSlide(prev => (prev + 1) % heroSlides.length);
    pauseAndResume();
  }, [heroSlides.length, pauseAndResume]);

  const goPrev = useCallback(() => {
    setHeroSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
    pauseAndResume();
  }, [heroSlides.length, pauseAndResume]);

  const goToSlide = useCallback((idx) => {
    setHeroSlide(idx);
    pauseAndResume();
  }, [pauseAndResume]);

  // Auto-scroll every 10 seconds (paused during manual interaction)
  useEffect(() => {
    if (heroSlides.length <= 1 || paused) return;
    const interval = setInterval(() => {
      setHeroSlide(prev => (prev + 1) % heroSlides.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [heroSlides.length, paused]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoResumeTimer.current) clearTimeout(autoResumeTimer.current);
    };
  }, []);

  // Touch/swipe handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight") goNext();
    else if (e.key === "ArrowLeft") goPrev();
  };

  const currentHero = heroSlides[heroSlide] || heroSlides[0] || [];

  if (!currentHero[0]) return null;

  return (
    <div className="relative mb-6 sm:mb-8" data-testid="hero-article"
      onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}
      onKeyDown={handleKeyDown} tabIndex={0} style={{ outline: "none" }}>

      <div
        key={heroSlide}
        className="grid grid-cols-1 lg:grid-cols-12 gap-0 rounded-xl overflow-hidden shadow-2xl"
        style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)", boxShadow: "0 8px 30px var(--color-accent-glow)", animation: "fadeIn 0.6s ease-in-out" }}
      >
        {/* Main Hero */}
        <div className="lg:col-span-8 group overflow-hidden grid grid-cols-1 sm:grid-cols-2"
          style={{ borderRight: "1px solid var(--color-border)" }} data-testid="hero-article-link">
          {currentHero[0].image_url && (
            <div className="aspect-[4/3] sm:aspect-auto sm:h-full overflow-hidden relative">
              <img src={currentHero[0].image_url} alt={currentHero[0].title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="eager" onError={(e) => { e.target.src = getFallbackImage(currentHero[0].category); }} />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-900/30 hidden sm:block"></div>
            </div>
          )}
          <div className="p-5 sm:p-6 lg:p-8 flex flex-col justify-center">
            {currentHero[0].category && (
              <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 text-[10px] sm:text-xs font-bold tracking-wider uppercase text-white rounded-full mb-3 self-start"
                style={{ backgroundColor: "var(--color-accent)", boxShadow: "0 4px 12px var(--color-accent-glow)" }} data-testid="hero-category">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                </span>
                BREAKING • {currentHero[0].category}
              </span>
            )}
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold tracking-tight leading-tight mt-1 mb-3"
              style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }} data-testid="hero-title">
              {currentHero[0].title}
            </h2>
            {currentHero[0].description && (
              <p className="text-sm leading-relaxed mb-3" style={{ color: "var(--color-text-muted)" }} data-testid="hero-description">
                {currentHero[0].description}
              </p>
            )}
            <button onClick={(e) => onArticleClick(currentHero[0], e)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold tracking-wide uppercase transition-all hover:brightness-110 self-start mb-3 cursor-pointer"
              style={{ backgroundColor: "var(--color-accent)", color: "#fff" }} data-testid="hero-read-more">
              Read More <ExternalLink className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center gap-3 text-sm" style={{ color: "var(--color-text-dim)" }}>
              <span className="font-semibold" style={{ color: "var(--color-live)" }} data-testid="hero-source">{currentHero[0].source}</span>
              <span>•</span>
              <span style={{ color: "var(--color-text-muted)" }}>{formatDate(currentHero[0].published_at).relativeTime}</span>
            </div>
          </div>
        </div>

        {/* Side Stories */}
        <div className="lg:col-span-4 flex flex-col" style={{ borderLeft: "1px solid var(--color-border)" }}>
          {currentHero.slice(1, 4).map((article, idx) => (
            <div key={article.url || idx} className="group flex-1 px-4 py-3 sm:px-5 sm:py-4"
              style={{ borderBottom: idx < 2 ? "1px solid var(--color-border)" : "none" }} data-testid={`side-article-${idx}`}>
              {article.category && (
                <span className="inline-block px-2.5 py-0.5 text-[9px] sm:text-[10px] font-bold tracking-wider uppercase rounded-full mb-1.5"
                  style={{ backgroundColor: "var(--color-accent-glow)", color: "var(--color-accent)", border: "1px solid var(--color-accent)" }}>
                  {article.category}
                </span>
              )}
              <h3 className="text-xs sm:text-sm font-semibold tracking-tight leading-snug mt-0.5 mb-1 line-clamp-2"
                style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
                {article.title}
              </h3>
              {article.description && (
                <p className="text-[11px] leading-relaxed mb-1.5 line-clamp-2" style={{ color: "var(--color-text-muted)" }}>
                  {article.description}
                </p>
              )}
              <button onClick={(e) => onArticleClick(article, e)}
                className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-wide hover:underline cursor-pointer"
                style={{ color: "var(--color-accent)", background: "none", border: "none", padding: 0 }} data-testid={`side-read-more-${idx}`}>
                Read More <ExternalLink className="w-3 h-3" />
              </button>
              <div className="flex items-center gap-2 text-[10px] sm:text-[11px] mt-1" style={{ color: "var(--color-text-dim)" }}>
                <span className="font-semibold" style={{ color: "var(--color-live)" }}>{article.source}</span>
                <span>•</span>
                <span style={{ color: "var(--color-text-muted)" }}>{formatDate(article.published_at).relativeTime}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation arrows */}
      {heroSlides.length > 1 && (
        <>
          <button onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110 z-10 backdrop-blur-sm"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", color: "#fff", border: "1px solid rgba(255,255,255,0.15)" }}
            data-testid="hero-prev-button">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110 z-10 backdrop-blur-sm"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", color: "#fff", border: "1px solid rgba(255,255,255,0.15)" }}
            data-testid="hero-next-button">
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Slide indicators + counter */}
      {heroSlides.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3" data-testid="hero-slide-indicators">
          <span className="text-[11px] font-bold mr-2" style={{ color: "var(--color-text-dim)" }}>
            {heroSlide + 1} / {heroSlides.length}
          </span>
          {heroSlides.map((_, idx) => (
            <button key={`slide-${idx}`} onClick={() => goToSlide(idx)}
              className="rounded-full transition-all duration-300"
              style={{
                width: idx === heroSlide ? "24px" : "8px", height: "8px",
                backgroundColor: idx === heroSlide ? "var(--color-accent)" : "var(--color-border)",
              }} data-testid={`hero-dot-${idx}`} />
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroCarousel;
